/**
 * Pure geometry of the linear dimensions drawn by the dimension tool — no ODA
 * here, so it can be unit-tested and shared by the dragger preview, the
 * created entity and the dimension-line handle.
 */

import type { Point3D } from "../types/types";

/**
 * What a dimension measures between its two points:
 * - horizontal: the X distance (OdDbRotatedDimension, rotation 0)
 * - vertical: the Y distance (OdDbRotatedDimension, rotation π/2)
 * - aligned: the true distance (OdDbAlignedDimension)
 */
export type DimensionKind = "horizontal" | "vertical" | "aligned";

export const DIMENSION_KINDS: readonly DimensionKind[] = [
  "aligned",
  "horizontal",
  "vertical",
];

/**
 * Distance between the dimension line and the measured points, as a multiple
 * of the dimension text height. Two heights leave room for the text written
 * above the line without it touching the measured geometry.
 */
export const DEFAULT_OFFSET_IN_TEXT_HEIGHTS = 2;

/** Rotation of an OdDbRotatedDimension, in radians. */
export function dimensionRotation(kind: "horizontal" | "vertical"): number {
  return kind === "horizontal" ? 0 : Math.PI / 2;
}

/** The distance the dimension displays. */
export function measuredDistance(
  kind: DimensionKind,
  a: Point3D,
  b: Point3D,
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  switch (kind) {
    case "horizontal":
      return Math.abs(dx);
    case "vertical":
      return Math.abs(dy);
    case "aligned":
      return Math.sqrt(dx * dx + dy * dy);
  }
}

/**
 * Unit normal of an aligned dimension, pointing "above" the measured segment:
 * upwards, or to the left when the segment is vertical.
 */
function alignedNormal(a: Point3D, b: Point3D): { x: number; y: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < 1e-12) return { x: 0, y: 1 };
  let nx = -dy / length;
  let ny = dx / length;
  if (ny < -1e-9 || (Math.abs(ny) <= 1e-9 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }
  return { x: nx, y: ny };
}

/**
 * Where the dimension line goes right after the second point is placed: above
 * the measured points (to their left for a vertical dimension, where "above"
 * means nothing), `offset` away from the outermost of the two.
 */
export function defaultDimLinePoint(
  kind: DimensionKind,
  a: Point3D,
  b: Point3D,
  offset: number,
): Point3D {
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  switch (kind) {
    case "horizontal":
      return { x: midX, y: Math.max(a.y, b.y) + offset, z: 0 };
    case "vertical":
      return { x: Math.min(a.x, b.x) - offset, y: midY, z: 0 };
    case "aligned": {
      const n = alignedNormal(a, b);
      return { x: midX + n.x * offset, y: midY + n.y * offset, z: 0 };
    }
  }
}

/**
 * The visible dimension line: from the foot of the first extension line to the
 * foot of the second. Any point of the line is a valid dimLinePoint, so this is
 * how the preview and the handle find where the line actually is.
 */
export function dimensionLineEnds(
  kind: DimensionKind,
  a: Point3D,
  b: Point3D,
  dimLinePoint: Point3D,
): [Point3D, Point3D] {
  switch (kind) {
    case "horizontal":
      return [
        { x: a.x, y: dimLinePoint.y, z: 0 },
        { x: b.x, y: dimLinePoint.y, z: 0 },
      ];
    case "vertical":
      return [
        { x: dimLinePoint.x, y: a.y, z: 0 },
        { x: dimLinePoint.x, y: b.y, z: 0 },
      ];
    case "aligned": {
      const n = alignedNormal(a, b);
      const along =
        (dimLinePoint.x - a.x) * n.x + (dimLinePoint.y - a.y) * n.y;
      return [
        { x: a.x + n.x * along, y: a.y + n.y * along, z: 0 },
        { x: b.x + n.x * along, y: b.y + n.y * along, z: 0 },
      ];
    }
  }
}

/**
 * Unit vector from the dimension line towards its text. With the text above
 * the line (DIMTAD), "above" follows the text reading direction: the line
 * direction brought into ]-90°, 90°], turned a quarter left — up for a
 * horizontal dimension, left for a vertical one.
 */
export function dimensionTextSide(
  kind: DimensionKind,
  a: Point3D,
  b: Point3D,
): { x: number; y: number } {
  if (kind === "horizontal") return { x: 0, y: 1 };
  if (kind === "vertical") return { x: -1, y: 0 };
  let angle = Math.atan2(b.y - a.y, b.x - a.x);
  if (angle > Math.PI / 2 + 1e-9) angle -= Math.PI;
  if (angle <= -Math.PI / 2 + 1e-9) angle += Math.PI;
  return { x: -Math.sin(angle), y: Math.cos(angle) };
}

/** Middle of the dimension line, under its text. */
export function dimensionLineMidpoint(
  kind: DimensionKind,
  a: Point3D,
  b: Point3D,
  dimLinePoint: Point3D,
): Point3D {
  const [start, end] = dimensionLineEnds(kind, a, b, dimLinePoint);
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2, z: 0 };
}

/**
 * Same formatting as the dimension style (2 decimals, comma, trailing zeros
 * dropped, no unit), for the live preview label.
 */
export function formatDimensionValue(value: number): string {
  return parseFloat(value.toFixed(2)).toString().replace(".", ",");
}
